/**
 * True when the knockouts UI should be revealed.
 */

/**
 * @typedef {Object} OptaContestant
 * @property {string} [name]
 */

/**
 * @typedef {Object} OptaMatchInfo
 * @property {{ name?: string }} [stage]
 * @property {OptaContestant[]} [contestant]
 */

/**
 * @typedef {Object} OptaMatch
 * @property {OptaMatchInfo} [matchInfo]
 * @property {string} [isoDate]
 */

const isKnockoutStage = (stageName) =>
  typeof stageName === 'string' && !stageName.includes('Group');

const hasBothContestants = (match) =>
  !!match?.matchInfo?.contestant?.length &&
  !!match.matchInfo.contestant[0]?.name &&
  !!match.matchInfo.contestant[1]?.name;

/**
 * Decide whether the knockout bracket UI should be revealed.
 *
 * Returns `true` iff every fixture in the first non-group stage (e.g.
 * Round of 32, Last 16) has both contestants named — i.e. not
 * placeholder phrases like "Winner Match 41". As soon as the opening
 * knockout round is fully populated with real teams, surface the
 * bracket regardless of date.
 *
 * @param {OptaMatch[]} matches  All matches from the Opta feed.
 * @returns {boolean}
 */
export default function shouldShowKnockouts(matches) {
  if (!Array.isArray(matches)) return false;

  const knockoutMatches = matches.filter((match) =>
    isKnockoutStage(match?.matchInfo?.stage?.name)
  );
  if (knockoutMatches.length === 0) return false;

  // Group knockout matches by stage name, then pick the stage that
  // kicks off earliest. That's the first round of the bracket.
  const byStage = new Map();
  for (const match of knockoutMatches) {
    const stageName = match.matchInfo.stage.name;
    if (!byStage.has(stageName)) byStage.set(stageName, []);
    byStage.get(stageName).push(match);
  }

  const earliestKickoff = (stageMatches) =>
    Math.min(
      ...stageMatches.map((m) => {
        const ts = m?.isoDate ? Date.parse(m.isoDate) : NaN;
        return Number.isFinite(ts) ? ts : Infinity;
      })
    );

  const firstStageMatches = [...byStage.values()].sort(
    (a, b) => earliestKickoff(a) - earliestKickoff(b)
  )[0];

  return firstStageMatches.every(hasBothContestants);
}
